import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMutation } from "react-query";
import moment from "moment";
import {
  Typography,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  IconButton,
  MenuItem,
  Menu,
  CircularProgress,
} from "@mui/material";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";

import * as api from "../../services";
import useScheduler from "../../Scheduler/useScheduler";
import CustomCalendar from "../../Scheduler/Calendar";
import { SchedulerProvider } from "../../Scheduler/schedulerContext";
import getEvent from "../../Scheduler/eventModel";
import DurationMenu from "../../Scheduler/Calendar/Components/DurationMenu";
import { dateFormat, dayFormat } from "../../tools";
import useInteraction from "../../UserSurveys/useInteractions";
import Dot from "../../Analyser/Board/Dot";
import MutationMessage from "../../Components/MutationMessage";
import AppContext from "../../appContext";
import TimePickerValue from "./TimePickerValue";

function Label({ id, name }) {
  return (
    <Box sx={{ display: "flex", marginRight: 2, textAlign: "left" }}>
      <Dot label={id} />
      <Typography noWrap variant="body2">
        {name}
      </Typography>
    </Box>
  );
}

function Labels({ list }) {
  // array of objects {id, name}
  //menu
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box display="flex" alignItems={"center"}>
      {/*    {list.length > 2 ? (
        <div style={{ minWidth: "fit-content" }}>
          <Button onClick={handleClick}>Toon labels</Button>{" "}
        </div>
      ) : null} */}
      <Menu
        id="surveys-menu"
        aria-labelledby="surveys-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {[...list]
          .filter((_, index) => ![0, 1].includes(index))
          .map((item) => (
            <MenuItem onClick={handleClose} key={item.id}>
              <Label id={item.id} name={item.name} />
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
}

// radio options
const OTHER_RADIO_ID = "other";
const TODAY_RADIO_ID = "today";

// options for hours and minutes
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
const MIN_OPTIONS = Array.from({ length: 60 }, (_, i) => i);

export default function PreviewSchedule({
  protocol,
  handleClose,
  handleBack,
  protocolDispatch,
}) {
  const {
    app: { sessionId },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const history = useHistory();

  // local state
  const [events, setEvents] = useState([]); // all events (client + protocol)
  const [clientEvents, setClientEvents] = useState(null); // client events
  const [isLoading, setIsLoading] = useState(true);
  const [option, setOption] = useState(null); // startDay toggle buttons
  const [delayHours, setDelayHours] = useState("0");
  const [delayMinutes, setDelayMinutes] = useState("0");

  // Server states
  // protocol
  const { scheduler: protocolScheduler } = useScheduler({
    id: protocol.protocolId,
  });
  const { interactions: protocolInteractions } = useInteraction({
    id: protocol.protocolId,
  });

  // client
  const { scheduler: clientScheduler } = useScheduler();
  const { interactions: clientInteractions } = useInteraction();

  // Display protocol on the calendar
  useEffect(() => {
    // make the calendar event to show the protocol on the calendar
    if (protocolScheduler && protocolInteractions) {
      const protocolEvents = protocolScheduler?.data?.map((event) =>
        getEvent({
          type: "SERVER_EVENT",
          payload: {
            event,
            interaction: {
              name: event.itemId
                ? protocolInteractions?.data?.filter(
                    (interaction) => interaction.id === event.itemId
                  )?.[0]?.name ?? ""
                : event.templateName,
              id: event?.itemId ?? "",
            },
            startDay: protocol.dateOffset,
            delay: protocol.timeShift,
          },
        })
      );
      const calendarEvents = [
        ...(clientEvents ?? []),
        ...(protocolEvents ?? []),
      ];
      setEvents(calendarEvents);
    }
  }, [protocol, protocolScheduler, clientEvents, protocolInteractions]);

  // Display the client interactions on the calendar
  useEffect(() => {
    if (clientScheduler?.data && clientInteractions) {
      const serverEventsClient = clientScheduler.data.map((event) =>
        getEvent({
          type: "SERVER_EVENT",
          payload: {
            event,
            interaction: clientInteractions.filter(
              (interaction) => interaction.id === event.itemId
            )?.[0] ?? {
              name: event.itemId,
              id: event.itemId,
            },
          },
        })
      );
      // style the events: make the client calendar events light grey
      const serverEventsClientsDecorations = serverEventsClient.map((beep) => ({
        ...beep,
        backgroundColor: "#89898930",
        textColor: "#bdbdbd",
      }));
      // filter out protocol id (prevent duplicated beeps in client calendar and protocol calendar)
      const serverEventsClientFiltered = serverEventsClientsDecorations.filter(
        (beep) => beep.protocolId !== protocol.protocolId
      );
      setClientEvents(serverEventsClientFiltered);
    }
  }, [clientScheduler, clientInteractions]);

  const handleStartDay = (value) => {
    const newDay = moment(value, dateFormat).format(dayFormat);
    protocolDispatch({ type: "SET_DATE_OFFSET", payload: newDay });
  };

  const resetDelay = () => {
    handleDelayHours(null, "00");
    handleDelayMinutes(null, "00");
  };

  const handleOption = (event) => {
    setOption(event.target.value);
    if (event.target.value === TODAY_RADIO_ID) {
      handleStartDay(moment.utc().format(dateFormat));
    }
    resetDelay();
  };

  const handleDateChange = (value) => {
    handleStartDay(value);
    handleOption({ target: { value: OTHER_RADIO_ID } });
    resetDelay();
  };

  const handleDelayHours = (_, value) => {
    setDelayHours(value);
    const hours = Number(value);
    const minutes = Number(delayMinutes);
    if (Number.isInteger(hours)) {
      const newDelay = hours * 60 * 60 + minutes * 60;
      protocolDispatch({ type: "SET_TIME_SHIFT", payload: newDelay });
    }
  };
  const handleDelayMinutes = (_, value) => {
    setDelayMinutes(value);
    const hours = Number(delayHours);
    const minutes = Number(value);
    if (Number.isInteger(minutes)) {
      const newDelay = hours * 60 * 60 + minutes * 60;
      protocolDispatch({ type: "SET_TIME_SHIFT", payload: newDelay });
    }
  };

  // INIT THE STARTDAY AND DELAY
  useEffect(() => {
    if (clientEvents && clientScheduler?.protocols && protocolScheduler?.data) {
      const newDay = clientScheduler?.protocols?.[protocol.protocolId]?.start
        ? moment
            .utc(clientScheduler.protocols[protocol.protocolId].start * 1000)
            .format(dayFormat)
        : moment.utc().format(dayFormat);
      const newDelay =
        clientScheduler?.protocols?.[protocol.protocolId]?.shift ?? 0;
      const newHour = moment.utc(newDelay * 1000).format("HH");
      const newMin = moment.utc(newDelay * 1000).format("mm");
      handleDelayHours(null, newHour.toString());
      handleDelayMinutes(null, newMin.toString());
      if (clientScheduler?.protocols?.[protocol.protocolId]?.start) {
        // init radio button
        setOption(OTHER_RADIO_ID);
      } else {
        setOption(TODAY_RADIO_ID);
      }
      // set global memory
      protocolDispatch({ type: "SET_DATE_OFFSET", payload: newDay });
      protocolDispatch({ type: "SET_TIME_SHIFT", payload: newDelay });
      setIsLoading(false);
    }
  }, [clientScheduler, clientEvents, protocolScheduler]);

  const saveMutation = useMutation(
    () => api.postProtocol({ ...protocol, sessionId }),
    {
      onSuccess: (data) => {
        if (data.data.status === 1) {
          handleClose();
          history.push("/scheduler");
        }
      },
    }
  );
  const handleSubmit = () => saveMutation.mutate();

  return (
    <div>
      <MutationMessage mutation={saveMutation} />
      <Box display="flex" justifyContent={"space-between"}>
        <Box display="flex" sx={{ gap: 1 }} alignItems="center">
          <IconButton onClick={handleClose} size="small" sx={{ padding: 1 }}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6">{t("common.buttons.schedule")}</Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<ScheduleSendIcon />}
          onClick={handleSubmit}
        >
          {t("protocol.start")}
        </Button>
      </Box>

      <Box padding={1} />

      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={520}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Box
            display="flex"
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ gap: 2 }}
          >
            <Box display={"flex"} alignItems={"center"} gap={2}>
              <Typography>{t("scheduler.start")}:</Typography>
              <FormControl>
                <RadioGroup
                  aria-labelledby="start-protocol"
                  name="radio-buttons-group"
                  row
                  value={option}
                  onChange={handleOption}
                >
                  <FormControlLabel
                    value={TODAY_RADIO_ID}
                    control={<Radio />}
                    label={t("timeline.buttons.today")}
                  />
                  <FormControlLabel
                    value={OTHER_RADIO_ID}
                    control={<Radio />}
                    label={
                      <DurationMenu
                        startAdornment={t("scheduler.on")}
                        selectedDate={protocol.dateOffset}
                        handleDateChange={handleDateChange}
                        minDate={moment.utc().startOf("day")}
                      />
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            <Box display={"flex"} alignItems={"center"} gap={2}>
              <Typography>{t("scheduler.delay")}:</Typography>
              <TimePickerValue
                value={delayHours}
                handleChange={handleDelayHours}
                options={HOUR_OPTIONS.map((option) => option + "")}
              />
              <Typography>{t("scheduler.hours")}</Typography>
              <TimePickerValue
                value={delayMinutes}
                handleChange={handleDelayMinutes}
                options={MIN_OPTIONS.map((option) => option + "")}
              />
              <Typography>{t("scheduler.min")}</Typography>
            </Box>
          </Box>
          <SchedulerProvider value={{ events, initEvents: events }}>
            <CustomCalendar
              variant="protocol"
              locked
              startDay={protocol.dateOffset}
              Labels={<Labels list={events} />}
            />
          </SchedulerProvider>
        </Box>
      )}
    </div>
  );
}
